import { useEffect, useRef, useState } from 'react';
import {
  Autocomplete,
  Avatar,
  Box,
  Checkbox,
  Chip,
  TextField,
} from '@mui/material';
import { CustomUser } from 'api/models/CustomUser';
import { getInitials } from 'utilities/getInitials';
import { UserAutocompleteProps } from './filters.props';
import { modelFiltersStyles } from './ModelFilters.styles';

const CHAR_WIDTH_PX = 7;   // approx px per character at ~0.8rem
const CHIP_PADDING_PX = 18; // left + right padding inside chip
const CHIP_GAP_PX = 4;      // gap between chips
const OVERFLOW_CHIP_PX = 36; // reserved width for "+N" chip

const estimateChipWidth = (name: string) => name.length * CHAR_WIDTH_PX + CHIP_PADDING_PX;

export const UserAutocomplete = ({
  options,
  value,
  onChange,
  onInputChange,
  inputValue,
  loading,
  label,
  placeholder,
  zoomLevel,
  textFieldSx = modelFiltersStyles.autocompleteTextField,
  disabled = false,
  maxVisibleTags = 2,
  showFullNames = false,
}: UserAutocompleteProps) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const [availableWidth, setAvailableWidth] = useState(300);

  useEffect(() => {
    if (!showFullNames) return;
    const el = rootRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() => {
      const inputBase = el.querySelector('.MuiInputBase-root');
      if (inputBase) {
        // Subtract space for the dropdown arrow button (~32px) and some padding
        setAvailableWidth(inputBase.clientWidth - 40);
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [showFullNames]);

  const getOptionLabel = (option: CustomUser) => {
    const fullName = [option.first_name, option.last_name].filter(Boolean).join(' ');
    return fullName || option.email || option.username || '';
  };

  const renderTags = (tagValue: CustomUser[]) => {
    if (tagValue.length === 0) return null;

    if (showFullNames) {
      // Dynamically compute how many full-name chips fit in the available width
      const { visibleCount } = tagValue.reduce(
        (acc, user) => {
          if (acc.done) return acc;
          const name = getOptionLabel(user);
          const chipW = estimateChipWidth(name);
          const gap = acc.visibleCount > 0 ? CHIP_GAP_PX : 0;
          const overflowReserve = tagValue.length > acc.visibleCount + 1 ? OVERFLOW_CHIP_PX + CHIP_GAP_PX : 0;
          if (acc.usedWidth + gap + chipW + overflowReserve > availableWidth) {
            return { ...acc, done: true };
          }
          return { visibleCount: acc.visibleCount + 1, usedWidth: acc.usedWidth + gap + chipW, done: false };
        },
        { visibleCount: 0, usedWidth: 0, done: false }
      );

      // Ensure at least 1 is visible
      const clampedCount = visibleCount === 0 ? 1 : visibleCount;
      const visibleItems = tagValue.slice(0, clampedCount);
      const remainingCount = tagValue.length - clampedCount;

      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'nowrap', overflow: 'hidden', flexShrink: 1, minWidth: 0 }}>
          {visibleItems.map(option => (
            <Chip
              key={option.id}
              label={getOptionLabel(option)}
              size="small"
              sx={{ ...modelFiltersStyles.chip, maxWidth: 'none', fontSize: '0.75rem' }}
            />
          ))}
          {remainingCount > 0 && (
            <Chip label={`+${remainingCount}`} size="small" sx={{ ...modelFiltersStyles.chip, flexShrink: 0 }} />
          )}
        </Box>
      );
    }

    // Default: avatar initials mode
    const visibleItems = tagValue.slice(0, maxVisibleTags);
    const remainingCount = tagValue.length - maxVisibleTags;

    return (
      <Box sx={{ ...modelFiltersStyles.getTagsContainer(tagValue.length), maxWidth: `${maxVisibleTags * 28 + 40}px` }}>
        {visibleItems.map(option => (
          <Avatar key={option.id} sx={modelFiltersStyles.avatar}>
            {getInitials({ name: `${option.first_name} ${option.last_name}` })}
          </Avatar>
        ))}
        {remainingCount > 0 && (
          <Chip label={`+${remainingCount}`} size="small" sx={modelFiltersStyles.chip} />
        )}
      </Box>
    );
  };

  return (
    <Box ref={rootRef}>
      <Autocomplete
        size="small"
        multiple
        options={options}
        getOptionLabel={getOptionLabel}
        slotProps={{
          popper: {
            sx: modelFiltersStyles.getAutocompletePopper(zoomLevel),
          },
        }}
        disableCloseOnSelect
        disabled={disabled}
        value={value}
        onChange={onChange}
        onInputChange={onInputChange}
        inputValue={inputValue}
        loading={loading}
        limitTags={-1}
        renderInput={params => (
          <TextField
            {...params}
            size="small"
            label={label}
            variant="outlined"
            placeholder={value.length === 0 ? placeholder : ''}
            sx={textFieldSx}
          />
        )}
        renderTags={renderTags}
        renderOption={(props, option, { selected }) => {
          const { key, ...optionProps } = props;
          return (
            <li key={option.id} {...optionProps} style={modelFiltersStyles.optionListItem}>
              <Checkbox style={modelFiltersStyles.optionCheckbox} checked={selected} />
              {[option.first_name, option.last_name].join(' ') || option.email || option.username || ''}
            </li>
          );
        }}
        sx={modelFiltersStyles.autocompleteRoot}
        isOptionEqualToValue={(option, val) => option.id === val.id}
      />
    </Box>
  );
};
